import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DashboardLayout from '../components/layout/DashboardLayout';
import api, { getApiError } from '../lib/axios';
import { Clock, Search, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import type { LookupHistoryItem } from '../types';

export default function History() {
  const navigate = useNavigate();
  const [lookups, setLookups] = useState<LookupHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const perPage = 20;

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await api.get('/api/lookup/history', { params: { page, per_page: perPage } });
        setLookups(res.data.lookups);
        setTotal(res.data.total);
      } catch (err: any) {
        setError(getApiError(err, 'Failed to load history'));
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [page]);

  const totalPages = Math.ceil(total / perPage);

  return (
    <>
      <Helmet><meta name="robots" content="noindex,nofollow" /></Helmet>
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Lookup History</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Your past domain lookups ({total} total)
          </p>
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
          </div>
        ) : lookups.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <Clock className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No lookups yet</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Start Searching
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domains Queried</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Results Found</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {lookups.map(lk => (
                    <tr key={lk.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-gray-900 dark:text-white">
                        {lk.created_at ? new Date(lk.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{lk.domains_queried}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{lk.results_count}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/dashboard?lookup=${lk.id}`)}
                          className="text-brand-600 dark:text-brand-400 hover:underline text-sm font-medium flex items-center gap-1"
                        >
                          <Search className="w-3.5 h-3.5" /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
    </>
  );
}
